/**
 * MQTT Logout Service (Kit19 SSO ecosystem).
 * Thin wrapper over mqttService for cross-app logout: publishes/receives
 * LOGOUT_EVENT messages on this device's logout topic so that logging out of
 * one Kit19 app on a device signs the user out of the rest.
 *
 * PORTABLE: copy as-is. The topic + payload here ARE the shared contract —
 * keep them identical across every microservice.
 */

import { initMqttWorker, subscribeTopic, publishMessage, onLogoutEvent as registerLogoutListener, getMqttStatus } from '../mqtt/mqttService';
import { getOrCreateDeviceId } from './deviceIdService';

/**
 * Connect (if needed) and subscribe to this device's logout topic so we
 * receive logout events published by other Kit19 apps on this device.
 */
export const initMqttLogoutClient = async () => {
  try {
    initMqttWorker(); // ensure the shared broker connection exists

    const deviceId = getDeviceId();
    const logoutTopic = `kit19/logout/${deviceId}`;
    await subscribeTopic(logoutTopic);
    return true;
  } catch (error) {
    console.error('[MQTT Logout Service] Initialization failed:', error);
    throw error;
  }
};

/** Get device ID (generate if not exists). Shared across all Kit19 apps. */
export const getDeviceId = () => {
  return getOrCreateDeviceId();
};

/** Publish a logout event for this device. */
export const publishLogoutEvent = async (deviceId, userId, appName) => {
  try {
    const logoutTopic = `kit19/logout/${deviceId}`;
    const logoutPayload = {
      type: 'LOGOUT_EVENT',
      deviceId,
      userId,
      appName,
      timestamp: new Date().toISOString(),
      action: 'logout_from_device'
    };

    await publishMessage(logoutTopic, logoutPayload, 1); // QoS as a number
    return true;
  } catch (error) {
    console.error('[MQTT Logout Service] Publish error:', error);
    throw error;
  }
};

/** Listen for logout events on this device. Returns an unsubscribe function. */
export const onLogoutEvent = (callback) => {
  try {
    return registerLogoutListener((data) => {
      if (typeof callback === 'function') {
        callback({
          deviceId: data.deviceId,
          userId: data.userId,
          appName: data.appName,
          timestamp: data.timestamp
        });
      }
    });
  } catch (error) {
    console.error('[MQTT Logout Service] Setup listener error:', error);
    return null;
  }
};

/** Get the logout topic for a device. */
export const getLogoutTopic = (deviceId) => {
  return `kit19/logout/${deviceId}`;
};

/** Whether the MQTT connection is currently up. */
export const isMqttConnected = () => {
  try {
    return getMqttStatus().connected === true;
  } catch {
    return false;
  }
};

/**
 * Auto-initialize MQTT on app load (call from App). Connects and subscribes
 * to this device's logout topic. Never throws — returns false on failure.
 */
export const autoInitMqtt = async () => {
  try {
    await initMqttLogoutClient();
    return true;
  } catch (error) {
    console.error('[MQTT Logout Service] Auto-init failed:', error);
    return false;
  }
};

const mqttLogoutServiceExport = {
  initMqttLogoutClient,
  getDeviceId,
  publishLogoutEvent,
  onLogoutEvent,
  getLogoutTopic,
  isMqttConnected,
  autoInitMqtt
};

export default mqttLogoutServiceExport;
